import update from 'immutability-helper';

import {
  EP_STORES_UPDATE_DELIVEROO_SITE,
  EP_STORES_UPDATE_DELIVEROO_SERVICE_HOURS,
  EP_STORES_UPDATE_DELIVEROO_STATUS,
  EP_STORES_UPDATE_JAHEZ_STORE,
  EP_STORES_GET_JAHEZ_STORE,
  EP_STORES_GET_DELIVEROO_SERVICE_HOURS,
  EP_STORES_GET_DELIVEROO_STATUS,
  EP_STORES_GET_DELIVEROO_SETTINGS
} from 'modules/endpoints';
import Fetcher from 'modules/service/fetcher';
import { TimeStringToShortTimeFormat } from 'utils/date-formatter';
import { setQueryData } from 'utils/react-query';

import { EP_STORES_LIST, EP_STORES_UPDATE, EP_STORES_GET_BY_ID } from './consts';
import {
  IDeliverooSiteSettings,
  IStoreDeliverooDetails,
  DeliverooSiteStatus,
  IStoreDeliveroServiceHoursDetails,
  IStoreDeliveroServiceStatus,
  IStoreJahezDetails,
  IWarehouseDetails,
  IWarehouseListItem,
  JahezSiteStatus
} from './types';

namespace Warehouses {
  export const list = async (): Promise<IWarehouseListItem[]> => await listWithPartnersStatus(false);

  export const listWithPartnersStatus = async (getJahezStoreStatus?: boolean, getDeliverooStoreStatus?: boolean): Promise<IWarehouseListItem[]> =>
    (await Fetcher.Get<{ items: IWarehouseListItem[] }>(EP_STORES_LIST, { getJahezStoreStatus, getDeliverooStoreStatus })).items;

  export const getById = async (id: string): Promise<IWarehouseDetails> => await Fetcher.Get(EP_STORES_GET_BY_ID, { id });

  export const getDeliverooServiceHours = async (id: string): Promise<IStoreDeliveroServiceHoursDetails> =>
    await Fetcher.Get(EP_STORES_GET_DELIVEROO_SERVICE_HOURS, { id });

  export const getDeliverooStatus = async (id: string): Promise<IStoreDeliveroServiceStatus> =>
    await Fetcher.Get(EP_STORES_GET_DELIVEROO_STATUS, { id });

  export const edit = async (requestData: IWarehouseDetails): Promise<void> => {
    await Fetcher.Post(EP_STORES_UPDATE, requestData);

    setQueryData<IWarehouseDetails>([EP_STORES_GET_BY_ID, requestData.id], () => requestData);
    setQueryData<IWarehouseListItem[]>([EP_STORES_LIST], (old) => {
      const itemIndex = old.findIndex((warehouse) => warehouse.id.toString() === requestData.id);
      const newItem: IWarehouseListItem = {
        ...old[itemIndex],
        schedule: requestData.serviceDays,
        status: requestData.status,
        etaBufferTime: requestData.etaBufferTime,
        serviceHours: {
          startTime: TimeStringToShortTimeFormat(requestData.serviceHoursStartTime),
          endTime: TimeStringToShortTimeFormat(requestData.serviceHoursEndTime)
        }
      };

      return update(old, { [itemIndex]: { $set: newItem } });
    });
  };

  export const editDeliverooStoreServiceHours = async (requestData: IStoreDeliveroServiceHoursDetails): Promise<void> => {
    await Fetcher.Post(EP_STORES_UPDATE_DELIVEROO_SERVICE_HOURS, requestData);
    setQueryData<IStoreDeliveroServiceHoursDetails>([EP_STORES_GET_DELIVEROO_SERVICE_HOURS, requestData.id], () => requestData);
  };

  export const editDeliverooStoreStatus = async (requestData: IStoreDeliveroServiceStatus): Promise<void> => {
    await Fetcher.Post(EP_STORES_UPDATE_DELIVEROO_STATUS, requestData);
    setQueryData<IStoreDeliveroServiceStatus>([EP_STORES_GET_DELIVEROO_STATUS, requestData.id], () => requestData);
    setQueryData<IWarehouseListItem[]>([EP_STORES_LIST], (old) => {
      const itemIndex = old.findIndex((warehouse) => warehouse.id.toString() === requestData.id);
      const newItem: IWarehouseListItem = {
        ...old[itemIndex],
        deliverooSiteStatus: requestData.deliverooSiteStatus ? DeliverooSiteStatus.Open : DeliverooSiteStatus.Closed
      };
      return update(old, { [itemIndex]: { $set: newItem } });
    });
  };

  export const editDeliverooSite = async (requestData: IDeliverooSiteSettings): Promise<void> => {
    await Fetcher.Post(EP_STORES_UPDATE_DELIVEROO_SITE, requestData);

    setQueryData<IStoreDeliverooDetails>([EP_STORES_GET_DELIVEROO_SETTINGS, requestData.id], (old) => ({
      ...old,
      deliverooSiteId: requestData.deliverooSiteId
    }));
  };

  export const getJahezStoreDetails = async (id: string): Promise<IStoreJahezDetails> =>
    await Fetcher.Get(EP_STORES_GET_JAHEZ_STORE, { id });

  export const editJahezStoreDetails = async (requestData: IStoreJahezDetails): Promise<void> => {
    await Fetcher.Post(EP_STORES_UPDATE_JAHEZ_STORE, requestData);

    setQueryData<IStoreJahezDetails>([EP_STORES_GET_JAHEZ_STORE, requestData.id], () => requestData);
    setQueryData<IWarehouseListItem[]>([EP_STORES_LIST], (old) => {
      const itemIndex = old.findIndex((warehouse) => warehouse.id.toString() === requestData.id);
      const newItem: IWarehouseListItem = {
        ...old[itemIndex],
        jahezSiteStatus: requestData.status ? JahezSiteStatus.Open : JahezSiteStatus.Closed
      };
      return update(old, { [itemIndex]: { $set: newItem } });
    });
  };
}

export default Warehouses;
export * from './types';
export * from './consts';
